import { insertMessagesSchema } from "@/api/db/schema/message";
import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import z from "zod";
import { getAllMessages } from "../../api/services/messages/get";
import { createMessage } from "../../api/services/messages/post";
import { authMiddlewareServer } from "../middleware/auth";

export const getAllMessagesHandler = createServerFn({ method: "GET" })
  .inputValidator(
    z.object({
      tagId: z.string().min(1, "Tag ID is required"),
    }),
  )
  .middleware([])
  .handler(async ({ data }) => {
    const messages = await getAllMessages(data.tagId);
    return {
      status: 200,
      body: { messages },
    };
  });

export const getAllMessagesOptions = ({ tagId }: { tagId: string }) =>
  queryOptions({
    queryKey: ["messages", tagId],
    queryFn: () => getAllMessagesHandler({ data: { tagId } }),
  });

function calculateTextLength(node: any): number {
  if (!node) return 0;
  let length = 0;

  if (node && typeof node.text === "string") {
    length += node.text.length;
  }
  if (node && node.children && node.children.length > 0) {
    for (const child of node.children) {
      length += calculateTextLength(child);
    }
  }
  if (node && node.content && node.content.length > 0) {
    for (const child of node.content) {
      length += calculateTextLength(child);
    }
  }

  return length;
}

export const createMessageHandler = createServerFn({ method: "POST" })
  .inputValidator(insertMessagesSchema)
  .middleware([authMiddlewareServer])
  .handler(async ({ context, data }) => {
    let messageLength = 0;

    try {
      if (data.messageData) {
        // Try parsing as JSON first
        if (
          data.messageData.trim().startsWith("{") &&
          data.messageData.trim().endsWith("}")
        ) {
          const messageObject = JSON.parse(data.messageData);

          if (messageObject && messageObject.root) {
            messageLength = calculateTextLength(messageObject.root);
          } else if (messageObject && messageObject.type === "doc") {
            messageLength = calculateTextLength(messageObject);
          } else {
            messageLength = data.messageData.replace(/<[^>]*>/g, "").length;
          }
        } else {
          messageLength = data.messageData.replace(/<[^>]*>/g, "").length;
        }
      }
    } catch (_e) {
      // Fallback to HTML tag stripping for text length
      messageLength = (data.messageData || "").replace(/<[^>]*>/g, "").length;
    }

    if (messageLength > 2000) {
      console.error("Exceeded maximum message length", messageLength);
      throw new Error("Message exceeds maximum length");
    }

    const res = await createMessage(data, context.auth.userId);
    return {
      status: 200,
      body: { success: res.ok },
    };
  });
